import { useState, useEffect, useRef, useCallback } from "react";

/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — GalleryV2 v2 (Jun 19)
   Swipe + Decode. Reads ?s= param to start at specific piece.
   ═══════════════════════════════════════════════════════════ */

const SCULPTURES = [
  {
    id: "bond-helix",
    bg: "#000000",
    name: "The Bond Helix",
    subtitle: "Two souls intertwined in crystal and gold",
    image: "/dna_cats_hero.png",
    madeFor: "Deepali",
    tags: ["Kathak dance", "Cancer research", "Cats", "Sisters"],
    discovery: "Deepali is a Kathak dancer and cancer researcher who adopted two cats — one dark, one amber. Her whole life is the double helix: science and art, discipline and grace, two things spiraling around each other that most people think don't belong together.",
    connections: [
      { element: "DNA double helix form", from: "Cancer research", match: "The helix is both the structure she studies and the invisible bond between two lives. Science made sculptural.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Two cats cradled at the core", from: "Her cats", match: "One dark, one amber — curled into each other at the heart of the helix. The bond they share is literally what holds the structure together.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Crystal butterfly wings", from: "Kathak dance", match: "A Kathak dancer's arms extend like wings mid-spin. Butterfly wings fan outward from a central axis. Both are symmetry in motion.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "roar-resonance",
    bg: "#000000",
    name: "The Roar and the Resonance",
    subtitle: "The stadium and the silence in one form",
    image: "/roar_resonance_hero.png",
    madeFor: "Pratik",
    tags: ["Chelsea FC", "Piano", "Hans Zimmer", "Football"],
    discovery: "Pratik contains both the stadium and the silence — the roar of a Chelsea goal and the quiet resonance of a piano key held down. Hans Zimmer scores both: the epic and the intimate.",
    connections: [
      { element: "Tidal wave form", from: "The roar of a goal", match: "A goal erupts like a wave — rising, cresting, crashing. The wave IS the crowd. The sculpture freezes the moment before it breaks.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Piano keys along the curve", from: "Piano", match: "Piano keys ripple along the wave like a soundtrack only he can hear. The keys and the wave both rise in scale — crescendo made visible.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Football at the crest", from: "Chelsea FC", match: "The ball hangs at the peak of the wave — the moment between strike and net. That suspended breath before everything erupts.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "metamorphosis",
    bg: "#000000",
    name: "The Metamorphosis Engine",
    subtitle: "Where raw power learns to fly",
    image: "/butterfly_guitar_hero.png",
    madeFor: "Marcus",
    tags: ["Guitar", "Motorcycles", "Self-made", "Transformation"],
    discovery: "Marcus started in a garage with a busted amp and a dream that didn't fit his town. The wheels kept turning, the music kept climbing. Somewhere between the grind and the grace, the thing he was building grew wings.",
    connections: [
      { element: "Butterfly form", from: "Transformation", match: "A butterfly is an engine that rebuilt itself from the inside. Marcus did the same — dismantled everything he was and emerged as something that flies.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Guitar neck as spine", from: "Guitar", match: "The guitar neck runs through the butterfly like a backbone — the thing that holds the wings together. His music is his structure.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Gear-textured body", from: "Motorcycles / Gearhead", match: "The butterfly's body is made of interlocking gears — mechanical precision inside organic beauty. The engine IS the butterfly.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "elephant",
    bg: "#000000",
    name: "The Moonbeam",
    subtitle: "Shashikala — the art of the moon",
    image: "/gallery/elephant.png",
    madeFor: "Shashikala",
    tags: ["Elephants", "Roses", "Education", "Cancer ♋"],
    discovery: "Her name — Shashikala — means 'moonlight' in Sanskrit. She's a Cancer, ruled by the moon. An ancient text connects the crescent moon to elephants. Her name, her zodiac, and her favorite animal all point to the same truth: she IS moonlight.",
    connections: [
      { element: "Book-page ears", from: "Education", match: "Elephant ears are fan-shaped. Open books are fan-shaped. Both receive and absorb knowledge.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Rose-petal skin", from: "Roses", match: "Rose petals overlap in layers. Elephant skin wrinkles overlap in layers. Both are armor made of something beautiful.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Crescent tusks", from: "Cancer / Moon", match: "Tusks curve in a crescent. The moon curves in a crescent. Both protect what they frame.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Rose-bloom trunk tip", from: "Roses", match: "Trunks reach upward and offer. Stems reach upward and offer. Both hold something precious at the tip.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "desert-tide",
    bg: "#000000",
    name: "The Desert Tide",
    subtitle: "Where sand meets sea meets soul",
    image: "/gallery/turtle.png",
    madeFor: "Lena",
    tags: ["Sea turtle", "Nursing", "Watercolors", "Desert", "Marathon", "Rocks"],
    discovery: "Lena grew up surrounded by desert but fell in love with the ocean — the opposite of everything she knew. She collects rocks from every place she visits, paints watercolor landscapes, and works the pediatric ICU at night. She's a Pisces, ruled by the moon and the sea. She belongs to both.",
    connections: [
      { element: "Topographic shell plates", from: "Rock collection / New Mexico desert / Landscape painting", match: "A sea turtle's shell is divided into scute plates — the same geometric sections as a topographic map. Each plate is a different landscape she has held: desert mesa, canyon ridge, coastal shelf. The shell IS her rock collection, arranged into something that carries her.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Watercolor-wash flippers", from: "Watercolor painting / Marathon running", match: "A sea turtle's flippers are long, sweeping, built for sustained movement across vast distances. A marathon runner's stride is the same — not explosive, but enduring. The flippers bleed color at their edges like wet watercolor on paper, the paint still moving, still going.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Pediatric hands cradling the underbelly", from: "Pediatric ICU nurse / Best friend", match: "Beneath the turtle, two open human hands emerge from the base — not gripping, just holding. The way a pediatric nurse holds the smallest patients through the night. The way a best friend holds without needing to explain why.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Crescent moon eye", from: "2am person / Pisces", match: "The turtle's eye is carved as a crescent moon — the symbol of the person who is most alive when the world is quietest. Pisces is ruled by the moon and the sea. She belongs to both.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "peacock",
    bg: "#000000",
    name: "The Sacred Fan",
    subtitle: "Where temple meets dance meets devotion",
    image: "/gallery/peacock.png",
    madeFor: "Priya",
    tags: ["Bharatanatyam", "Temple architecture", "Peacocks"],
    discovery: "Priya designs temples and dances Bharatanatyam — both are acts of devotion made physical. The peacock is India's national bird, and its display is the most architectural gesture in nature.",
    connections: [
      { element: "Gopuram tail feathers", from: "Temple architecture", match: "Peacock feathers fan in radiating arcs. Temple gopurams rise in radiating tiers. Both are sacred geometry made visible.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Dancer's neck curve", from: "Bharatanatyam", match: "The peacock's neck curves in a perfect S. A dancer's arm in anjali mudra curves in a perfect S. Both are grace encoded in line.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Spire crown", from: "Temple architecture", match: "The peacock's crown rises in pointed plumes. A gopuram spire rises in pointed tiers. Both mark the highest point of something sacred.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "octopus",
    bg: "#000000",
    name: "The Cartographer",
    subtitle: "Eight arms reaching for every star",
    image: "/gallery/octopus.png",
    madeFor: "Aisha",
    tags: ["Octopus", "Violin", "Vintage maps", "Stargazing"],
    discovery: "Aisha studies the ocean and maps the stars — she lives between two infinities. The octopus is the most intelligent creature in the sea, and its tentacles reach in every direction like lines on a celestial chart.",
    connections: [
      { element: "Violin-scroll tentacle tips", from: "Violin", match: "Tentacles curl in a spiral at the tip. Violin scrolls curl in a spiral at the head. Both are elegance at the point of contact.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Compass-rose mantle", from: "Vintage maps", match: "The octopus mantle is a smooth dome. A globe is a smooth dome. Both contain entire worlds inside a curved surface.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Star-cluster suckers", from: "Stargazing", match: "Suckers dot the tentacles in rows. Stars dot the sky in constellations. Both are patterns scattered across a dark surface.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "cathedral",
    bg: "#000000",
    name: "The Matcha Requiem",
    subtitle: "Every morning ritual is a prayer",
    image: "/gallery/cathedral.png",
    madeFor: "Tomoko",
    tags: ["Piano", "Gothic cathedrals", "Cherry blossoms", "Hourglasses"],
    discovery: "Tomoko plays piano in cathedrals and drinks ceremonial matcha every morning. Her whole life is ritual — repetition that becomes transcendence.",
    connections: [
      { element: "Piano-key columns", from: "Piano", match: "Cathedral columns rise vertically in alternating rhythm. Piano keys rise in alternating black and white. Both ascend in scale.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Hourglass rose window", from: "Hourglasses", match: "The rose window is a circle of intricate detail. An hourglass cross-section is a circle of suspended time. Both freeze a moment inside a frame.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Cherry blossom buttresses", from: "Cherry blossoms", match: "Flying buttresses arc outward, delicate but structural. Cherry blossom branches arc outward, delicate but alive.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
  {
    id: "whale",
    bg: "#000000",
    name: "The Ink and Tide",
    subtitle: "A life written across an ocean",
    image: "/gallery/whale.png",
    madeFor: "Sana",
    tags: ["Calligraphy", "Whales", "Archery", "Aquarius ♒"],
    discovery: "Sana writes in Arabic and Japanese — two scripts that flow like water. She loves whales, practices archery, and is an Aquarius. Her whole world is about precision within flow.",
    connections: [
      { element: "Calligraphy-skin markings", from: "Arabic & Japanese calligraphy", match: "Flowing script inscribes a page. Barnacles and scars inscribe a whale's body. Both are lifetimes written across a surface.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Longbow tail flukes", from: "Archery", match: "Whale flukes sweep in a wide recurve shape. A longbow sweeps in a wide recurve shape. Both store immense tension in an elegant line.", score: "Geometric ✦ Functional ✦ Emotional" },
      { element: "Perfume-bottle body", from: "Perfume collecting", match: "A whale's body is smooth, curved, and carries something precious inside. A perfume bottle is smooth, curved, and carries something precious inside.", score: "Geometric ✦ Functional ✦ Emotional" },
    ],
  },
];

const ACCENT_PALETTES = [
  { primary: "#c89b4a", glow: "rgba(200,155,74,0.3)" },
  { primary: "#8264c8", glow: "rgba(130,100,200,0.3)" },
  { primary: "#4a8ec2", glow: "rgba(74,142,194,0.3)" },
  { primary: "#78788c", glow: "rgba(120,120,140,0.3)" },
  { primary: "#5b7fc7", glow: "rgba(91,127,199,0.3)" },
  { primary: "#3da87a", glow: "rgba(61,168,122,0.3)" },
  { primary: "#4a6db5", glow: "rgba(74,109,181,0.3)" },
  { primary: "#6b9e7a", glow: "rgba(107,158,122,0.3)" },
  { primary: "#4a7db5", glow: "rgba(74,125,181,0.3)" },
];

function getStartIndex() {
  try {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("s");
    if (s !== null) {
      const n = parseInt(s);
      if (!isNaN(n) && n >= 0 && n < SCULPTURES.length) return n;
    }
  } catch (e) {}
  return 0;
}

function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => { const h = () => setM(window.innerWidth <= bp); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, [bp]);
  return m;
}

export default function Gallery() {
  const [idx, setIdx] = useState(getStartIndex);
  const [decodeOpen, setDecodeOpen] = useState(false);
  const [expandedEl, setExpandedEl] = useState(null);
  const [entering, setEntering] = useState(true);
  const [direction, setDirection] = useState(0);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const isMobile = useIsMobile();
  const s = SCULPTURES[idx];
  const pal = ACCENT_PALETTES[idx];
  const bgColor = s.bg || "#000000";

  useEffect(() => {
    setEntering(true); setDecodeOpen(false); setExpandedEl(null);
    const t = setTimeout(() => setEntering(false), 400);
    return () => clearTimeout(t);
  }, [idx]);

  const go = useCallback((dir) => {
    setDirection(dir); setEntering(true);
    setTimeout(() => {
      setIdx(prev => {
        const next = prev + dir;
        if (next < 0) return SCULPTURES.length - 1;
        if (next >= SCULPTURES.length) return 0;
        return next;
      });
    }, 50);
  }, []);

  const onTouchStart = (e) => { touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) { dx < 0 ? go(1) : go(-1); }
  };

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") go(1);
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") go(-1);
      if (e.key === "Escape") setDecodeOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go]);

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: bgColor, fontFamily: '"Outfit", sans-serif', position: "relative", color: "#e8e6e2", transition: "background 0.8s ease" }}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        html,body{margin:0;padding:0;overflow:hidden;background:${bgColor};transition:background 0.8s ease}*{box-sizing:border-box}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:rgba(201,169,110,0.3);border-radius:4px}
      `}</style>

      {/* Header */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "calc(env(safe-area-inset-top, 0px) + 14px) 20px 14px" : "20px 40px" }}>
        <a href="/" style={{ fontSize: 16, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", color: "rgba(232,230,226,0.7)", textDecoration: "none" }}>Yours Truly</a>
        <a href="/create" style={{ fontSize: 11, color: "rgba(201,169,110,0.8)", textDecoration: "none", padding: "8px 20px", borderRadius: 20, border: "1px solid rgba(201,169,110,0.25)", fontWeight: 500, letterSpacing: "0.05em" }}>Create yours — $29</a>
      </div>

      {/* Main sculpture view */}
      <div style={{ position: "absolute", inset: 0, zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isMobile ? "80px 20px 160px" : "80px 60px 140px", opacity: entering ? 0 : 1, transform: entering ? "translateX(" + direction * 30 + "px)" : "translateX(0)", transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)" }}>
        <div style={{ flex: 1, minHeight: 0, width: "100%", maxWidth: 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={s.image} alt={s.name} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", borderRadius: 16, filter: "drop-shadow(0 20px 60px rgba(0,0,0,0.6))" }} />
        </div>
        <div style={{ textAlign: "center", marginTop: 20, width: "100%" }}>
          <div style={{ fontSize: 9, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,169,110,0.5)", marginBottom: 6 }}>Made for {s.madeFor}</div>
          <div style={{ fontSize: isMobile ? 26 : 36, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontWeight: 300, lineHeight: 1.15, marginBottom: 4 }}>{s.name}</div>
          <div style={{ fontSize: 13, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", color: "rgba(232,230,226,0.45)" }}>{s.subtitle}</div>
        </div>
      </div>

      {/* Dot indicators */}
      <div style={{ position: "absolute", bottom: isMobile ? 115 : 100, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 8, zIndex: 15 }}>
        {SCULPTURES.map((_, i) => (
          <button key={i} onClick={() => { setDirection(i > idx ? 1 : -1); setIdx(i); }}
            style={{ width: i === idx ? 20 : 6, height: 6, borderRadius: 3, border: "none", cursor: "pointer", background: i === idx ? "rgba(201,169,110,0.8)" : "rgba(255,255,255,0.15)", transition: "all 0.3s ease" }} />
        ))}
      </div>

      {/* Arrow nav desktop */}
      {!isMobile && (
        <>
          <button onClick={() => go(-1)} style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", zIndex: 15, width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
          <button onClick={() => go(1)} style={{ position: "absolute", right: 20, top: "50%", transform: "translateY(-50%)", zIndex: 15, width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.4)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
        </>
      )}

      {/* Decode toggle */}
      <button onClick={() => setDecodeOpen(!decodeOpen)} style={{ position: "absolute", bottom: isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 20px)" : 28, left: "50%", transform: "translateX(-50%)", zIndex: 25, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 24, background: decodeOpen ? "rgba(201,169,110,0.15)" : "rgba(255,255,255,0.06)", border: "1px solid " + (decodeOpen ? "rgba(201,169,110,0.4)" : "rgba(255,255,255,0.1)"), color: decodeOpen ? "rgba(201,169,110,0.9)" : "rgba(232,230,226,0.6)", fontSize: 12, fontWeight: 500, fontFamily: '"Outfit", sans-serif', letterSpacing: "0.05em", backdropFilter: "blur(20px)", transition: "all 0.3s ease" }}>
        <span style={{ fontSize: 14 }}>✦</span>
        {decodeOpen ? "Hide" : "Decode this sculpture"}
      </button>

      {/* Decode panel */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 22, maxHeight: decodeOpen ? (isMobile ? "75vh" : "55vh") : 0, opacity: decodeOpen ? 1 : 0, transform: decodeOpen ? "translateY(0)" : "translateY(40px)", transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)", overflow: "hidden", pointerEvents: decodeOpen ? "auto" : "none" }}>
        <div style={{ background: "rgba(10,10,18,0.95)", backdropFilter: "blur(30px)", borderTop: "1px solid rgba(201,169,110,0.15)", borderRadius: "24px 24px 0 0", padding: isMobile ? "24px 20px calc(env(safe-area-inset-bottom, 0px) + 80px)" : "28px 40px 90px", maxWidth: 700, margin: "0 auto", overflowY: "auto", maxHeight: isMobile ? "75vh" : "55vh" }}>
          <div style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,169,110,0.5)", marginBottom: 10 }}>The Discovery</div>
          <div style={{ fontSize: 14, lineHeight: 1.7, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", color: "rgba(232,230,226,0.7)", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>{s.discovery}</div>
          <div style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,169,110,0.5)", marginBottom: 12 }}>How it became anatomy</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {s.connections.map((c, i) => {
              const open = expandedEl === i;
              return (
                <div key={i} onClick={() => setExpandedEl(open ? null : i)} style={{ borderRadius: 14, cursor: "pointer", padding: "14px 16px", background: open ? "rgba(201,169,110,0.06)" : "rgba(255,255,255,0.02)", border: "1px solid " + (open ? "rgba(201,169,110,0.2)" : "rgba(255,255,255,0.04)"), transition: "all 0.3s ease" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 15, fontFamily: '"Cormorant Garamond", serif', fontWeight: 500, color: "#e8e6e2", marginBottom: 2 }}>{c.element}</div>
                      <div style={{ fontSize: 10, color: "rgba(201,169,110,0.6)", letterSpacing: "0.05em" }}>from "{c.from}"</div>
                    </div>
                    <div style={{ color: "rgba(201,169,110,0.4)", fontSize: 14, transition: "transform 0.3s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>›</div>
                  </div>
                  <div style={{ maxHeight: open ? 200 : 0, opacity: open ? 1 : 0, overflow: "hidden", transition: "all 0.4s ease", marginTop: open ? 12 : 0 }}>
                    <div style={{ fontSize: 13, lineHeight: 1.7, fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", color: "rgba(232,230,226,0.55)", marginBottom: 8 }}>{c.match}</div>
                    <div style={{ fontSize: 9, color: "rgba(201,169,110,0.4)", letterSpacing: "0.1em" }}>{c.score}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.04)" }}>
            {s.tags.map((tag, i) => (
              <span key={i} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 12, background: "rgba(201,169,110,0.06)", border: "1px solid rgba(201,169,110,0.12)", color: "rgba(201,169,110,0.6)" }}>{tag}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Swipe hint mobile */}
      {isMobile && idx === 0 && !decodeOpen && (
        <div style={{ position: "absolute", bottom: 70, left: "50%", transform: "translateX(-50%)", zIndex: 10, fontSize: 10, color: "rgba(255,255,255,0.2)", animation: "fadeIn 1s ease 2s both", letterSpacing: "0.1em" }}>swipe to explore</div>
      )}
    </div>
  );
}
